import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { DxPivotGridComponent, DxLoadPanelComponent, DxDataGridComponent, DxTabPanelComponent } from 'devextreme-angular';
import { exportPivotGrid, exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { NotificationService } from 'src/app/common/component/notification/notification.service';
import { CRMReportRequest } from '../crm-data-by-zip-territory.component';
import { CrmDataByZipTerritoryService } from '../crm-data-by-zip-territory.service';
import { DataGridCreatedEvent, isDecompressRequired } from '../crm-report-datagrid/crm-report-datagrid-defns';
import saveAs from 'file-saver';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import * as chroma from "chroma-js";
import * as wkt from 'wkt';
import * as geojson from "geojson";
import * as bbox from "geojson-bbox";
import { BehaviorSubject, interval, take, timer } from 'rxjs';
import { Utility } from 'src/app/common/service/utility';
import { TextDataResponse } from 'src/app/common/model/TextDataResponse';
import { toJpeg} from 'html-to-image';

export class MapConfig {
  stage: string
  displayName: string
  viewportDivID: string
  legendDivID: string
}

export class ExportStatus {
  allOppty: boolean
  openOppty: boolean
}

@Component({
  selector: 'app-crm-report-opportunities',
  templateUrl: './crm-report-opportunities.component.html',
  styleUrls: ['./crm-report-opportunities.component.scss']
})
export class CrmReportOpportunitiesComponent implements OnInit {

  @Input()
  reportRequest: CRMReportRequest;
  
  @ViewChild('crmChildTabPanel', { static: false }) tabPanel: DxTabPanelComponent;   

  @ViewChild('OpenOpptyByVol', { static: false }) openOpptyByVolGrid: DxPivotGridComponent;  
  @ViewChild('openOppty_loadpanel', { static: false }) openOppty_loadpanel: DxLoadPanelComponent;
  @ViewChild('allstagesMap_loadpanel', { static: false }) allstagesMap_loadpanel: DxLoadPanelComponent;    

  childDataGrids: Array<DataGridCreatedEvent> = new Array<DataGridCreatedEvent>();

  mapConfigs_Template: MapConfig[] = [
    {stage: "All", displayName: "All Sales Stages", 
      viewportDivID: "allstages-map-viewport", legendDivID: "allstages-map-legend"},
    {stage: "Negotiation", displayName: "Negotiation Sales Stage", 
      viewportDivID: "neogotiation-map-viewport", legendDivID: "neogotiation-map-legend"},
    {stage: "Proposal", displayName: "Proposal Sales Stage", 
      viewportDivID: "proposal-map-viewport", legendDivID: "proposal-map-legend"},
    {stage: "Needs Assessment", displayName: "Needs Assessment Sales Stage", 
      viewportDivID: "needsAssess-map-viewport", legendDivID: "needsAssess-map-legend"}, 
    {stage: "Qualification", displayName: "Qualification Sales Stage", 
      viewportDivID: "qualification-map-viewport", legendDivID: "qualification-map-legend"},
    {stage: "Prospecting", displayName: "Prospecting Sales Stage", 
      viewportDivID: "prospecting-map-viewport", legendDivID: "prospecting-map-legend"},
  ];

  mapConfigs: MapConfig[] = [];

  openOppty_heading1: string = '';
  allOppty_heading1: string = '';

  pivotGrid_OpenOpptyByVol_Datasource: any; 

  maxRetries = 1;
  retryCount = 0;

  loader: Loader;
  google: any;
  infoWindow: google.maps.InfoWindow; 
  bounds: any;
  terrCountyMapInfo: any;
  countyGeometryData: Map<string, any> = new Map();
  maps: Map<string, google.maps.Map> = new Map();
  showMaps = false;
  showInfoText = false;
  

  countyMTD: Array<{county:string, stage: string, MTD: number}> = new Array();  
  hideLegend_allStages: boolean = true;

  loadingPanelVisible = false;
  workbook: any;
  excelColMaxWidth = 30;
  excelColMinWidth = 13;  

  tabIndexAtExport: number = 0;
  exportStatus: ExportStatus = {allOppty: true, openOppty: false};
  exportStatus$ = new BehaviorSubject<ExportStatus>(this.exportStatus);

  constructor(private notificationService: NotificationService, 
    private crmDataByZipTerritoryService: CrmDataByZipTerritoryService,
    private changeDetectorRef: ChangeDetectorRef, private utility: Utility) { }

  ngOnInit(): void {
    this.setReportHeadings();
    this.getOpenOpptyDetails();
  }

  ngAfterViewInit(): void {
    this.openOppty_loadpanel.visible = true;
    this.allstagesMap_loadpanel.visible = true;

    //This loads the google javascript API once.
    new Loader({
      apiKey: environment.gmaps,
      version: 'weekly'
    }).load().then((google) => {
      this.google = google;
    });
    
  }

  setReportHeadings(){    
    this.setReportHeadings_OpenOppty(); 
    this.setReportHeadings_AllOppty();
  }

  setReportHeadings_OpenOppty(){
    if(this.reportRequest.territoryValue){
      this.openOppty_heading1 = "CRM 'Open' Opportunities in the Geography of " + this.reportRequest.territoryValue + " by Volume";
    }else if(this.reportRequest.postCodeValue){
      this.openOppty_heading1 = 'Opportunities within ' + String(this.reportRequest.radiusMiles) + ' miles of ' + this.reportRequest.postCodeValue + " by Volume";
    }
  }

  setReportHeadings_AllOppty(){
    if(this.reportRequest.territoryValue){
      this.allOppty_heading1 = "CRM 'All' Opportunities in the Geography of " + this.reportRequest.territoryValue + " by Volume";
    }else if(this.reportRequest.postCodeValue){
      this.allOppty_heading1 = 'Opportunities within ' + String(this.reportRequest.radiusMiles) + ' miles of ' + this.reportRequest.postCodeValue + " by Volume";
    }
  }

  getOpenOpptyDetails(){

    this.crmDataByZipTerritoryService.getCRMReport(this.reportRequest, 'Oppty_OpenOpptyDetails').subscribe({
      next: async (crmReportData_openOpptyDetails: any) => {            
        
        if(isDecompressRequired("Oppty_OpenOpptyDetails")){
          var decompressedData = this.utility.decompressData(crmReportData_openOpptyDetails as TextDataResponse);
          crmReportData_openOpptyDetails = JSON.parse(decompressedData);
        }

        this.getTerrCountyMapInfo();

        await this.customizeForDisplay(crmReportData_openOpptyDetails);

        this.setOpenOpptyByVol_Datasource(crmReportData_openOpptyDetails);        
        this.openOppty_loadpanel.visible = false;
                       
        let openOpptyByVol_created_event: DataGridCreatedEvent = {
          reportType: "Oppty_OpenOpptyByVol", 
          dataGridComponent: this.openOpptyByVolGrid
        };

        this.onDataGridCreated(openOpptyByVol_created_event);           

      },
      error: (errorResponse: any) => {

        if(errorResponse.status == 504){ // if gateway timeout error
          if(this.retryCount < this.maxRetries){ // attempt a retry if max retries not reached
            this.retryCount++;
            this.getOpenOpptyDetails();
            return;
          }
        }
        this.openOppty_loadpanel.visible = false;
        this.notificationService.alert("error", "Error while fetching CRM Report data", errorResponse);
      }
    });

  }

  customizeForDisplay(crmReportData_openOpptyDetails: any){

    crmReportData_openOpptyDetails.forEach((item,i) => {

      this.countyMTD.push({
        county: item.region + '-' + item.countyPrefecture,
        stage: item.salesStage,
        MTD: this.roundToTwoDecimal(item.mtd)
      });

      //ex: Convert 'Proposal' to '50::Proposal'
      //This is done so that in pivotgrid the column is ordered by salesStagePct desc.
      //Later in the onCellPrepared method, we will reformat the header text properly.
      item.salesStage = (item.salesStagePct as number).toString().padStart(2,'0') + '::' + item.salesStage;

      
    });    

  }

  roundToTwoDecimal(num: number) {
    return Math.round((num + Number.EPSILON) * 100) / 100
  }

  getTotMTDByCounty(stage: string) {    
    return this.countyMTD
      .filter(item => (stage == 'All')? true: item.stage == stage)
      .reduce((acc, item) => {
        return acc.set(item.county, this.roundToTwoDecimal((acc.get(item.county) ?? 0) + item.MTD));      
      }, new Map<string, number>());
  }

  setOpenOpptyByVol_Datasource(crmReportData_openOpptyDetails: any){
    this.pivotGrid_OpenOpptyByVol_Datasource = {
      fields: [{
        caption: 'Territory from CRM',
        width: 120,
        dataField: 'crmTerritory',
        expanded: true,        
        area: 'row',
      }, {
        caption: 'Sales Stage / Sales Stage Pct',
        width: 120,
        dataField: 'salesStage',
        area: 'column', 
        sortOrder: 'desc'
      }, 
      {
        caption: 'Num of Opptys',
        dataField: 'salesStage',
        dataType: 'string',
        summaryType: 'count',
        area: 'data',
        format: { type: 'fixedPoint', precision: 0 },
        wordWrapEnabled: false
      },{
        caption: 'MTD',        
        dataField: 'mtd',
        dataType: 'number',
        summaryType: 'sum',
        area: 'data',
        format: { type: 'fixedPoint', precision: 0 }
      }],
      store: crmReportData_openOpptyDetails
    }
  }

  onCellPrepared_openOpptyByVol($event){

    //Reformat the column header cell text. Ex: 50::Proposal will be formatted to Proposal \n\n 50%.
    if($event.area == 'column' && $event.cell.type == 'D'){
      let hdrTxt = $event.cell.text.split("::");
      let cellE: HTMLElement = $event.cellElement;      
      cellE.innerHTML = hdrTxt[1] + "<br/><br/>" + parseInt(hdrTxt[0]) + '%';
    }
  }

  getTerrCountyMapInfo(){

    //Call API to get county-prefecture data. we get an array of {countyname, its polygon coords}
    this.crmDataByZipTerritoryService.getTerrCountyMapInfo(this.reportRequest).subscribe({
      next: terrCountyMapInfo => {
        if(terrCountyMapInfo.length > 0){
          this.terrCountyMapInfo = terrCountyMapInfo;        
          this.initMap();
        }else {
          let map = this.mapConfigs_Template.find(map => map.stage == 'All');
          this.mapConfigs.push(map);
          this.showMaps = true;
          this.showInfoText = true;
          this.changeDetectorRef.detectChanges();

          this.mapConfigs.forEach(map => {
            let mapArea = document.getElementById(map.viewportDivID);
            mapArea.innerHTML = "Unable to generate Map for this territory. Map information is not available.";
          });
        }
      },
      error: (error: any) => {
        this.allstagesMap_loadpanel.visible = false;
        this.notificationService.alert("error", "Error while fetching CRM Territory Map Info", error);
      }
    });

  }

  getBreaksAndColors(values: number[]): {breaks: number[], breaks_legend: string[], colors: string[]}{
    //the input 'values' is an array of numbers using which the map will be color coded; Ex MTD values.
    //For the given 'values', we will generate the breaks and colors array.

    // Ex: If MTD Values = [0.39,0.76,1.52,1.62,2.11,2.48,16.22,19.71]
    // Then, breaks = [1,4,7,11,14,17,20]
    // And, breaks_legend = ["0-1","1-4","4-7","7-11","11-14","14-17","17-20"]
    // And, colors = ["#008000","#80c000","#ffff00","#ffd200","#ffa500","#ff5200","#ff0000"]

    //sort input and remove dupes
    let valuesUniq = Array.from(new Set(values.sort((a,b) => a-b)));

    //n is the number of breaks to be generated; maximum is 7
    let n = (valuesUniq.length >= 7)? 7 : valuesUniq.length;

    let breaks = [];
    let colors = [];
    let breaks_legend = [];

    if(n != 0){
      //generate 'n' equidistant breaks and round them up. Refer to https://gka.github.io/chroma.js/#chroma-limits
      breaks = Array.from(new Set(chroma.limits(valuesUniq, 'e', n-1)
                                    .sort((a,b) => a-b)
                                    .map(mtd => Math.ceil(mtd))));

      //In some cases where MTD value is 0
      if(breaks[0] == 0){
        //If 0 is the only value we replace it with 1, so that legend shows as 0-1. otherwise it will show as 0-0 (wrong).
        if(breaks.length == 1) {
          breaks[0] = 1;
        } else{ //there are other values, just remove zero in the first position and legends will be generated fine.
          breaks = breaks.slice(1);
        }
        
      }

      let colorBands = [];
      if(breaks.length <= 2){
        colorBands = ['green','yellow'];
      }else if(breaks.length <= 4){
        colorBands = ['green','yellow','orange'];
      }else {
        colorBands = ['green','yellow','orange', 'red'];
      }
      //generate equidistant colors. Refer to https://gka.github.io/chroma.js/#color-scales
      colors = chroma.scale(colorBands).colors(breaks.length);

      //generate the breaks legend labels
      breaks.forEach((val,i) => {
        if(i == 0 && breaks[0] != 0) {breaks_legend.push(`0 - ${val}`)}
        else {breaks_legend.push(`${breaks[i-1]} - ${breaks[i]}`)}
      });
    }

    return {breaks: breaks, breaks_legend: breaks_legend, colors: colors};
  }

  async initMap(){

    //Parse the geometry of each county and store it once. This will avoid parsing for each map.
    await this.parseGeomtery();

    let noOpenOpportunities_allStages = this.getTotMTDByCounty('All').size == 0;

    //If there are no open opportunities, generate only the All Stages map.
    if(noOpenOpportunities_allStages){
      let map = this.mapConfigs_Template.find(map => map.stage == 'All');
      this.mapConfigs.push(map);      
    }else{ //otherwise generate all maps.
      this.mapConfigs = [...this.mapConfigs_Template];      
    }

    this.showMaps = true;
    this.changeDetectorRef.detectChanges();
    this.mapConfigs.forEach(map => {
      this.generateMap(this.getTotMTDByCounty(map.stage), map.viewportDivID, map.legendDivID, map.stage);
    });
    
    
  }

  parseGeomtery(){
    this.terrCountyMapInfo.forEach((county,i) => {
      //The geo-data from API is returned as 'well-known-text' format.
      //Use 'wkt' library to parse it to geometry object. Refer to https://www.npmjs.com/package/wkt
      let geometry = wkt.parse(county.geogReduce);

      if(geometry.type == "GeometryCollection"){
        geometry.geometries = geometry.geometries.filter(geo => geo.type != "Point");
      }

      this.countyGeometryData.set(county.countyPrefectureCombined, geometry);

    });
  }

  generateMap(countyMTD: Map<string, number>, divID_map: string, divID_legend: string, stage: string) {

    //For the MTD values, generate breaks and colors
    const { breaks, breaks_legend,  colors} = this.getBreaksAndColors(Array.from(countyMTD.values()));

    //Set the Legend
    let mapLegend = document.getElementById(divID_legend) as HTMLElement;

    if(countyMTD.size > 0){
      colors.forEach((color,i) => {
        const div = document.createElement("div");
        div.innerHTML = `<svg height="20" width="50">  <rect width="40" height="20" style="stroke-width:3;fill:${color}" /> </svg>` + `${breaks_legend[i]}`;
        mapLegend.appendChild(div);
      });
    }else{ //i.e if there are no open opportunities..

      //Set legend text accordingly.
      const div = document.createElement("div");
      if(stage == 'All'){
        div.innerHTML = "<p>There are no open opportunities.</p>"
      }else{
        div.innerHTML = "<p>There are no open opportunities in " 
          + this.mapConfigs.find(map => map.stage == stage).displayName + ".</p>";
      }      
      mapLegend.appendChild(div);
      
    }

    //Given a county name, determine the color code based on its MTD value. This function will be used down the line.
    const getColor = (county: string): string => {

      let mtd = countyMTD.get(county);

      if(mtd || mtd == 0){

        if(mtd == 0) return colors[0];        
        return colors[breaks.findIndex(n => n >= mtd)];

      }else{
        return "#FFFFFF";
      }
      
    }

    //Function to get the MTD of the county. will be used down the line.
    const getMTD = (county: string): number => {
      return countyMTD.get(county);
    }

    let map: google.maps.Map = new this.google.maps.Map(document.getElementById(divID_map) as HTMLElement, {
      zoom: 7,
      mapTypeId: "terrain",
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,      
      disableDoubleClickZoom: true
    });      
    
    let territory_geoData: Array<any> = new Array<any>();      

    this.countyGeometryData.forEach((geometry, county) => {
      territory_geoData.push({
        county: county,
        MTD: getMTD(county),
        fillColor: getColor(county),
        geo: geometry
      });
    });

    //Parse the geo-data array into GeoJSON and create a Feature Collection object.
    // References -
    // 1. https://www.npmjs.com/package/geojson
    // 2. https://geojson.org/geojson-spec.html#examples
    let featureCollection = geojson.parse(territory_geoData, {GeoJSON: 'geo'});      

    //Generate a bounding box for this feature collection. will not be visible in the map.
    //Use this bounding box to determine the center point and set it as the map view's center.      

    if(!this.bounds){
      let boundingBox = bbox(featureCollection);
      this.bounds = new google.maps.LatLngBounds();      
      this.bounds.extend({lat: boundingBox[1], lng: boundingBox[0]});
      this.bounds.extend({lat: boundingBox[3], lng: boundingBox[2]});
    }
    
    map.fitBounds(this.bounds);
    map.setCenter(this.bounds.getCenter());
    map.setZoom(7);

    //Add the geo-json to the map. This will generate all the polygons in the map (i.e counties)
    map.data.addGeoJson(featureCollection);

    this.hideLegend_allStages = false;

    //Set the color codes for the polygons. we already determined the fill color for the county and added it as a property to the feature.
    // now we just get the fillColor property from the feature.      
    map.data.setStyle(function(feature) {        
      return {
        strokeColor: "#4d4d4d",
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: feature.getProperty("fillColor"),
        fillOpacity: 1.0,      
      }
    });

    const handleCountyClick = (event) => {
      
      let feature = event.feature;
      let mtd = feature.getProperty("MTD");
      if (!mtd && mtd != 0) return;    
  
      let county = feature.getProperty("county").split("-");
      let content = '<span style="font-size:small">County: ' + `${county[1]}, ${county[0]}` +
          '<br/> MTD: ' + feature.getProperty("MTD") +        
          '</span>';    
  
      if(this.infoWindow){
        this.infoWindow.close();
      }
      this.infoWindow = new this.google.maps.InfoWindow();
      this.infoWindow.setContent(content);
      this.infoWindow.setPosition(event.latLng);
      this.infoWindow.open(map);
    }
    
    //Listener for click event, to display info when user clicks on a county.
    map.data.addListener('click', handleCountyClick);

    this.maps.set(stage, map);

  }

  async exportExcel(e) {

    this.loadingPanelVisible = true;

    //Before export reset the map view incase user has altered it (zoom pan etc). Otherwise the exported map view will not be correct.
    this.maps.forEach((map, stage) => {
      map.fitBounds(this.bounds);
      map.setCenter(this.bounds.getCenter());
    });

    //Before export, switch to Open Oppty tab (if not switched by user), then scroll down to bottom.
    //This is because, the google map image tiles are lazy-loaded ONLY when the map is scrolled into view.
    //If the user never scrolled all the way down, but does an export, then we only get blank images during export.
    //So as a work-around, we do a programmatic scroll before export.    
    this.tabIndexAtExport = this.tabPanel.selectedIndex;
    if(this.tabIndexAtExport != 1){
      this.tabPanel.selectedIndex = 1;
    }

    interval(1000).pipe(take(this.mapConfigs.length)).subscribe(i => {
      document.getElementById(this.mapConfigs[i].legendDivID).scrollIntoView();
    });

    //close any open info window, before export.
    if(this.infoWindow){
      this.infoWindow.close();
    }

    this.workbook = new Workbook();

        

    //Map export takes some time. So when it completes, it sends an event. 
    //We subscribe to it. When both all oppty and open oppty worksheets are ready, then we write to workbook and download.
    this.exportStatus$.subscribe(exportStatus => {
      if(exportStatus.allOppty && exportStatus.openOppty){

        this.scrollToTop();
        this.tabPanel.selectedIndex = this.tabIndexAtExport;
        this.loadingPanelVisible = false;

        this.workbook.xlsx.writeBuffer()
        .then(function (buffer: BlobPart) {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'cdbOpportunityMaster.xlsx');
        });

        //Reset export status (if exported once more, we don't want this observer to think that it is ready)
        this.exportStatus.allOppty = false;
        this.exportStatus.openOppty = false;
      }
    });

    //Export all Opptys Tab first. this finishes quickly.
    let allOpptysDetails_datagrid_idx = this.childDataGrids.findIndex(event => event.reportType == 'OpptysWith91MA_AllOpptysDetails');

    if(allOpptysDetails_datagrid_idx >= 0){
      await this.createAllOpptyDetails_worksheet(this.childDataGrids[allOpptysDetails_datagrid_idx].dataGridComponent);
    }

    //Then export Open Opptys Tab. this takes time.
    //Time delay is added as a precaution just in case, above scroll to bottom etc take some time.
    timer(8000).subscribe(() => {
      this.createOpenOpptys_worksheet();
    });

  }

  //Export All Opptys Details tab
  async createAllOpptyDetails_worksheet(dataGrid: DxDataGridComponent, columnFormatDetails?: Array<any>){

    const worksheet = this.workbook.addWorksheet('All Opptys');

    await exportDataGrid({
      component: dataGrid.instance,
      worksheet: worksheet,
      topLeftCell: { row: 2, column: 1 },
    }).then((cellRange) => {

      //Set default alignment and font properties at the worksheet level
      worksheet.eachRow((row, i) => {
        row.alignment = {
          vertical: 'top',          
          wrapText: true
        }

        row.font = {
          name: 'Tahoma',
          size: 10
        }

        row.border = {
          top: {style:'thin', color: {argb:'000000'}},
          left: {style:'thin', color: {argb:'000000'}},
          bottom: {style:'thin', color: {argb:'000000'}},
          right: {style:'thin', color: {argb:'000000'}}
        };
      });        
      
      
    
    }).then(() => {
      
      let parser = new DOMParser();

      const opptyIDColumn = worksheet.getColumn(4);

      opptyIDColumn.eachCell((cell, rowNum) => {
        if(rowNum > 2){
          let parsedHtml = parser.parseFromString(cell.value, 'text/html');
          let anchorElement = parsedHtml.getElementsByTagName("a")[0];
          
          cell.value = {
            text: anchorElement.textContent,
            hyperlink: anchorElement.getAttribute('href'),
            tooltip: anchorElement.getAttribute('href')
          };
          
          cell.font = { name: 'Tahoma', size: 10, color: { argb: '0000ff' }, underline: true };
        }
      });

      const acctnumColumn = worksheet.getColumn(5);

      acctnumColumn.eachCell((cell, rowNum) => {
        if(rowNum > 2){
          let parsedHtml = parser.parseFromString(cell.value, 'text/html');
          let anchorElement = parsedHtml.getElementsByTagName("a")[0];
          
          cell.value = {
            text: anchorElement.textContent,
            hyperlink: anchorElement.getAttribute('href'),
            tooltip: anchorElement.getAttribute('href')
          };    
          
          cell.font = { name: 'Tahoma', size: 10, color: { argb: '0000ff' }, underline: true };
        }
      });

    }).then(() => {

      //Prepare Sheet header-1
      worksheet.mergeCells('A1:S1');      
      worksheet.getCell('A1').value = {
        'richText': [
          {'font': {'size': 18,'color': {'argb': '1C3A70'},'name': 'Tahoma'},'text': this.allOppty_heading1},          
        ]
      };

      worksheet.getCell('A1').alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true
      }
      
      worksheet.getRow(1).height = 24; 

      //Set format for table header row
      const tableHeaderRow = worksheet.getRow(2);
      
      tableHeaderRow.fill = {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'1C3A70'},
      };                  

      tableHeaderRow.font = { name: 'Tahoma', size: 11, color: { argb: 'ffffff' }, bold: true };
      tableHeaderRow.height = 40;
      
      //fill style
      const header_fill_1 = {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'6A5ACD'},
      };            

      //Change fill color and border for certain header cells
      [29,30,31,32,33].forEach((i) => {
        tableHeaderRow.getCell(i).fill = header_fill_1;
      });      

      const header_fill_2 = {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'0000FF'},
      };

      //Change fill color for certain header cells
      [34,35,36].forEach((i) => {        
        tableHeaderRow.getCell(i).fill = header_fill_2;
      });

      const header_fill_3 = {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'00008B'},
      };

      //Change fill color for certain header cells
      [59,60,61,62].forEach((i) => {        
        tableHeaderRow.getCell(i).fill = header_fill_3;
      });

      //fill style
      const header_fill_4 = {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'B0C4DE'},
      };            

      //Change fill color for certain header cells (from col 69 to col 159)
      for(let i=69;i<=159;i++) {
        tableHeaderRow.getCell(i).fill = header_fill_4;
        tableHeaderRow.getCell(i).font = { name: 'Tahoma', size: 11, color: { argb: '000000' }, bold: true };
      };

      //Adjust column widths for better appearance      
      worksheet.columns.forEach((column,i) => {

        if(column.width > this.excelColMaxWidth){
          column.width = this.excelColMaxWidth;
        }

        //adjust column width based on the header text length
        if(i > 37 && (!column.width || column.width < this.excelColMinWidth)){
          if(tableHeaderRow.getCell(i).value.length > 16){            
            column.width = 18;
          }else{
            column.width = this.excelColMinWidth;
          }
          
        }
      });

    }).then(() => {
      //Finally we send the event that allOppty worksheet is filled.
      this.exportStatus.allOppty = true;
      this.exportStatus$.next(this.exportStatus);
    });

  }

  async createOpenOpptys_worksheet(){

    let worksheet = null;

    let openOpptyByVol_datagrid_idx = this.childDataGrids.findIndex(event => event.reportType == 'Oppty_OpenOpptyByVol');    

    if(openOpptyByVol_datagrid_idx >= 0){
      worksheet = this.workbook.addWorksheet('Open Opptys');
    }else{
      return;
    }

    let excelRow_Last = 1;

    if(openOpptyByVol_datagrid_idx >= 0){      

      await exportPivotGrid({
        component: this.childDataGrids[openOpptyByVol_datagrid_idx].dataGridComponent.instance,
        worksheet: worksheet,
        topLeftCell: { row: 2, column: 1 },
        customizeCell: this.customizeCell_openOpptyByVol
      }).then((cellRange) => {
        //Pivotgrid export freezes the first few columns. We don't want that, so we unfreeze.
        worksheet.views = [{state: 'normal'}];
        excelRow_Last = cellRange.to.row;
        
      }).then(() => {

        //Some standard style definitions to re-use further below.
        let stdHeaderFill =  {
          type: 'pattern',
          pattern:'solid',
          fgColor:{argb:'1C3A70'},          
        };

        let stdBorder = {
          top: {style:'thin', color: {argb:'000000'}},
          left: {style:'thin', color: {argb:'000000'}},
          bottom: {style:'thin', color: {argb:'000000'}},
          right: {style:'thin', color: {argb:'000000'}}
        }

        let stdHeaderFont = { name: 'Arial', size: 10, color: { argb: 'ffffff' }, bold: true };

        //Unmerge these cells and add the column headers to A2 and B2 cells
        worksheet.unMergeCells('A2');

        let cellA2Properties = {
          value: 'Territory from CRM',
          fill: stdHeaderFill,
          font: stdHeaderFont,
          alignment: { vertical: 'top', horizontal: 'left', wrapText: true }          
        }

        Object.assign(worksheet.getCell('A2'),cellA2Properties);

        let cellB2Properties = {
          value: 'Sales Stage/ Sales Stage Pct:',
          fill: stdHeaderFill,
          border: stdBorder,
          font: stdHeaderFont,
          alignment: { vertical: 'top', horizontal: 'left', wrapText: true }
        };

        Object.assign(worksheet.getCell('B2'), cellB2Properties);
        
        worksheet.getRow(2).height = 50;
        worksheet.getColumn(2).width = 15.5;

        //Merge the top row cells and add the grid level header
        worksheet.mergeCells('A1:G1');      
        worksheet.getCell('A1').value = {
          'richText': [
            {'font': {'size': 18,'color': {'argb': '1C3A70'},'name': 'Tahoma'},'text': this.openOppty_heading1},          
          ]
        };

        worksheet.getCell('A1').alignment = {
          vertical: 'top',
          horizontal: 'left',
          wrapText: true
        }
        
        worksheet.getRow(1).height = 24;
      }).then(async () => {

        // Export Maps as images.

        let images: Array<{imageId: number, type: "map" | "legend", elementId: string}> = new Array();
        let promiseArray: Array<Promise<{elementId: string, type: "map" | "legend", dataURL: string}>> = new Array();

        //For each map to be exported, convert the map and its legend to image.
        //This call returns promises and we hold them in an array.
        this.mapConfigs.forEach(map => {

          const promise1 = this.convertElementToImage(map.viewportDivID, "map");
          const promise2 = this.convertElementToImage(map.legendDivID, "legend");

          promiseArray.push(promise1,promise2);

        });

        //Then we wait for all promises to resolve. The conversion to image takes some time.
        //Each promise resolves to an object of type {elementId: string, type: "map" | "legend", dataURL: string}        
        Promise.all(promiseArray).then((values) => {

          let imageId = 0;

          //Here dataURL is the base64 format of the jpeg image. 
          //Adding image to workbook is a two step process. Fist step, add it to workbook and it gives back an imageId.          
          values.forEach(async obj => {
            imageId = await this.workbook.addImage({
              base64: obj.dataURL,
              extension: 'jpeg',
            });

            //Store all imageIds in an array, in next step these imageIds will be added to worksheet.
            images.push({imageId: imageId, elementId: obj.elementId, type: obj.type});
          });

        }).then(() => {
          let tlColMap = 0;
          let brColMap = 7; 

          let images_OrderedArray = new Array();

          //We donot know in what order the promises resolved. But for each map, 
          //we need the map image followed by its corresponding legend image. So we order them first, 
          //based on what order they appear in the mapconfig.
          this.mapConfigs.forEach(map => {
            images_OrderedArray.push(images.find(image => image.elementId == map.viewportDivID));
            images_OrderedArray.push(images.find(image => image.elementId == map.legendDivID));
          });
          
          //Then add the images one by one to worksheet.
          //In this way 'map' image will be placed first, and corresponding 'legend' image on its side.
          //Then the next 'map' image and 'legend' image and so on.
          images_OrderedArray.forEach(image => {

            let tlCol: number, brCol: number;
            let tlRow: number, brRow: number;

            //Add the display name above each map.
            if(image.type == 'map'){
              let mapHdrRowNum = excelRow_Last + 3;              
              worksheet.mergeCells(`A${mapHdrRowNum}:G${mapHdrRowNum}`);      
              worksheet.getCell(`A${mapHdrRowNum}`).value = {
                'richText': [
                  {'font': {'size': 14,'color': {'argb': '1C3A70'},
                    'name': 'Tahoma'},'text': this.mapConfigs.find(map => map.viewportDivID == image.elementId).displayName},          
                ]
              };

              worksheet.getCell(`A${mapHdrRowNum}`).alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
              }

              worksheet.getCell(`A${mapHdrRowNum}`).fill = {
                type: 'pattern',
                pattern:'solid',
                fgColor:{argb:'f2f2f2'},
              }; 
              
              worksheet.getRow(mapHdrRowNum).height = 20;
            }
             
            //Determine the positions where the map and legend images need to be placed in Excel.
            if(image.type == 'map'){
              tlCol = tlColMap;
              brCol = brColMap;
              tlRow = excelRow_Last + 3;
              brRow = excelRow_Last + 23
            }else if(image.type == "legend"){
              tlCol = brColMap + 0.5;
              brCol = brColMap + 3;
              tlRow = excelRow_Last + 3;
              brRow = excelRow_Last + 11;
              //We extend the last row number by 23, so next map starts at that position.
              excelRow_Last = excelRow_Last + 23;
            }

            //Add the image to the worksheet.
            worksheet.addImage(image.imageId, {            
              tl: { col: tlCol, row: tlRow },
              br: { col: brCol, row: brRow }
            });
          });
        }).then(() => {
          //Finally we send the event that openOppty worksheet is filled.
          this.exportStatus.openOppty = true;
          this.exportStatus$.next(this.exportStatus);
        });
            
      });
    }
  }

  convertElementToImage(elementId: string, type: "map" | "legend") {
    return toJpeg(document.getElementById(elementId), { skipFonts: true, quality: 0.8, includeQueryParams: true})
      .then(dataUrl => {
        return {elementId: elementId, type: type, dataURL: dataUrl };
      });
  }  

  customizeCell_openOpptyByVol = ({pivotCell, excelCell}) => {
          
    //We use this function to apply as much formatting as possible.
    // Because the 'pivotCell' object has some properties which indicate
    // what type of cell it is (data cell or row header or column header or grand total etc).
    // Otherwise it is difficult to identify using normal exceljs row/column/cell functions.


    //Apply formatting for each data cell (including grand total cells)
    let dataCellFmt = {
      numFmt: '#,##0',
      alignment: { vertical: 'middle', horizontal: 'center' }
    };

    if(this.isDataCell(pivotCell) || this.isGrandTotalDataCell(pivotCell)){
      excelCell.value = pivotCell.value? pivotCell.value as Number : pivotCell.value;
      Object.assign(excelCell, dataCellFmt);
    }

    //Apply some more formatting for each grand total cell
    let gtcellFormat = {
      fill: {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'FFEBCD'},
      },
      font: { name: 'Arial', size: 10, color: { argb: '000000' }, bold: true }
    };

    if(this.isAnyGrandTotalCell(pivotCell)){
      Object.assign(excelCell, gtcellFormat);
      if(pivotCell.area == 'row'){
        Object.assign(excelCell,{alignment: { vertical: 'middle', horizontal: 'left' }});
      }
    }

    //Apply bg-colors for row headers (CRM Territory and FY)
    let rowCellFormat1 = {
      fill: {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'B0C4DE'},
      }
    };

    let rowCellFormat2 = {
      fill: {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'D3D3D3'},
      }
    };

    if(pivotCell.area == 'row' && pivotCell.type == 'D'){
      if(pivotCell.columnIndex == 0){
        Object.assign(excelCell, rowCellFormat1);
      }else if(pivotCell.columnIndex == 1){
        Object.assign(excelCell, rowCellFormat2);
      }            
    };
    
    //Apply some formatting for column header cells (Q1, Q2 etc..)
    let colHeaderFormat1 =  {
      fill: {
        type: 'pattern',
        pattern:'solid',
        fgColor:{argb:'1C3A70'},
      },
      font: { name: 'Arial', size: 10, color: { argb: 'ffffff' }, bold: true }
    };

    if(pivotCell.area == 'column' && pivotCell.type == 'D'){

      let hdrTxt = pivotCell.text.split("::");           
      excelCell.value = hdrTxt[1] + "\n" + parseInt(hdrTxt[0]) + '%';

      Object.assign(excelCell, colHeaderFormat1);
    }
    
    //Apply border format for all cells in the pivot grid
    let cellBorder = {
      border: {
        top: {style:'thin', color: {argb:'000000'}},
        left: {style:'thin', color: {argb:'000000'}},
        bottom: {style:'thin', color: {argb:'000000'}},
        right: {style:'thin', color: {argb:'000000'}}
      }
    };

    Object.assign(excelCell, cellBorder);
  }

  isDataCell(cell) {
    return (cell.area === 'data' && cell.rowType === 'D' && cell.columnType === 'D');
  }

  isGrandTotalDataCell(cell) {
    return (cell.area === 'data' && (cell.type === 'GT' || cell.rowType === 'GT' || cell.columnType === 'GT'));
  }

  isRowOrColTotalDataCell(cell){
    return (cell.area === 'data' && (cell.rowType === 'T' && cell.columnType != 'GT') || (cell.columnType === 'T') && cell.rowType != 'GT');
  }

  isAnyGrandTotalCell(cell){
    return (cell.type === 'GT' || cell.rowType === 'GT' || cell.columnType === 'GT');
  }  


  //Receive datagrid created events and store the event meta-data.
  //This will be used for the export-to-excel functionality
  onDataGridCreated(event: DataGridCreatedEvent){       
    this.childDataGrids.push(event);
  }

  scrollToTop(){
    let top = document.getElementById("scrollToTop");
    top.scrollIntoView({behavior: "smooth"});
  }

}
